import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import {
  Card, CardContent, CardHeader, CardTitle, CardDescription,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Plus, Pencil, Trash2, ExternalLink } from "lucide-react";
import { useAllPostsForAdmin, Post } from "@/hooks/usePosts";
import PostEditor from "./PostEditor";
import { format } from "date-fns";
import { toast } from "sonner";
import { Link } from "react-router-dom";

interface PostManagerProps {
  authorMemberId: string | null;
}

const PostManager = ({ authorMemberId }: PostManagerProps) => {
  const [editing, setEditing] = useState<Post | null | "new">(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const { data: posts, isLoading, refetch } = useAllPostsForAdmin(refreshKey);

  const handleSaved = () => {
    setEditing(null);
    setRefreshKey((k) => k + 1);
    refetch();
  };

  const handleDelete = async (id: string) => {
    const { error } = await supabase.from("posts").delete().eq("id", id);
    if (error) toast.error(error.message);
    else {
      toast.success("Post deleted");
      setRefreshKey((k) => k + 1);
    }
  };

  if (editing !== null) {
    return (
      <PostEditor
        post={editing === "new" ? null : editing}
        authorMemberId={authorMemberId}
        onClose={() => setEditing(null)}
        onSaved={handleSaved}
      />
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <CardTitle>Impact Stories</CardTitle>
            <CardDescription>Create and manage blog posts.</CardDescription>
          </div>
          <Button onClick={() => setEditing("new")}>
            <Plus className="h-4 w-4 mr-2" /> New Post
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <p className="text-muted-foreground">Loading...</p>
        ) : !posts || posts.length === 0 ? (
          <p className="text-muted-foreground text-center py-8">
            No posts yet. Click "New Post" to create your first story.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Title</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Chapter</TableHead>
                  <TableHead>Updated</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {posts.map((p) => (
                  <TableRow key={p.id}>
                    <TableCell className="font-medium">{p.title}</TableCell>
                    <TableCell>
                      <Badge variant={p.status === "published" ? "default" : "secondary"}>
                        {p.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="capitalize">{p.chapter}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {format(new Date(p.updated_at), "MMM d, yyyy")}
                    </TableCell>
                    <TableCell className="text-right space-x-1">
                      {p.status === "published" && (
                        <Button asChild variant="ghost" size="icon" title="View">
                          <Link to={`/impact/stories/${p.slug}`} target="_blank">
                            <ExternalLink className="h-4 w-4" />
                          </Link>
                        </Button>
                      )}
                      <Button variant="ghost" size="icon" onClick={() => setEditing(p)} title="Edit">
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant="ghost" size="icon" title="Delete">
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete this post?</AlertDialogTitle>
                            <AlertDialogDescription>
                              "{p.title}" will be permanently removed. This cannot be undone.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction onClick={() => handleDelete(p.id)}>
                              Delete
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default PostManager;
